import { useCallback, useEffect, useMemo, useState } from '@frontend/react';
import { useSecureSession } from '@frontend/react-domain/secureSessions';
import { useAuthSlice } from '@frontend/redux';
import { Header, LoadingSpinnerPanel, Modal, Text, YStack, showToast } from '@frontend/web-react';
import { captureSentryMessage } from '@frontend/web-utils';
import { useDispatchApi } from '@frontend/web/hooks';
import { disconnectSocket, getSocket, initializeSocket } from '@frontend/web/utils/sockets';

type SecureSessionLiveViewProps = {
  onClose: () => void;
  secureNoteId: string;
  taskId?: string;
};

export const SecureSessionLiveView = ({ onClose, secureNoteId, taskId }: SecureSessionLiveViewProps) => {
  const { token } = useAuthSlice();
  const { invalidateCache } = useDispatchApi();
  const [secureSessionUrl, setSecureSessionUrl] = useState<string | null>(null);

  const handleError = useCallback(
    (e: Error) => {
      showToast({
        message: e.message,
        title: 'Error',
        variant: 'danger',
      });
      setSecureSessionUrl(null);
      onClose();
      captureSentryMessage(`An error occurred while connecting to the secure session: ${e.message}`, 'error');
    },
    [onClose],
  );

  const handleSuccess = useCallback(() => {
    showToast({
      autoClose: 8000,
      message: 'Secure session launched successfully',
      title: 'Success',
      variant: 'success',
    });
    invalidateCache([
      { id: secureNoteId, prefix: 'secureNoteId', type: 'SecureNote' },
      { id: 'LIST', type: 'SecureNote' },
    ]);
    setSecureSessionUrl(null);
    onClose();
  }, [onClose]);

  const socketClient = useMemo(
    () => ({
      disconnectSocket,
      getSocket,
      initializeSocket,
    }),
    [],
  );

  const { abandonSession, liveViewUrl } = useSecureSession(
    {
      authToken: token!,
      onError: handleError,
      onSuccess: handleSuccess,
      secureNoteId,
      taskId,
    },
    socketClient,
  );

  useEffect(() => {
    if (liveViewUrl) {
      setSecureSessionUrl(liveViewUrl);
    }
  }, [liveViewUrl]);

  const handleClose = useCallback(() => {
    abandonSession();
    setSecureSessionUrl(null);
    onClose();
  }, [abandonSession, onClose]);

  return (
    <YStack gap="md">
      {secureSessionUrl ? (
        <Modal
          Header={
            <Header onClickRightIcon={handleClose} rightIconName="IconX" title="Secure Session">
              <Text color="secondary">
                Log in and complete 2FA as you usually would.
                Be sure to select any &quot;Remember me,&quot; &quot;Keep me signed in,&quot; or &quot;Trust this device&quot; option.
                You may need to acknowledge/close cookie policy pop-ups. You may also have to click several times into the text fields in order to paste.
                Click save once you&apos;re fully authenticated, and then our copilots will be able to access your session.
              </Text>
            </Header>
          }
          fullScreen
          isOpen={!!secureSessionUrl}
          onClose={handleClose}
        >
          <iframe
            allow="clipboard-read; clipboard-write;"
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
            src={secureSessionUrl}
            style={{
              height: '90vh',
              width: '99vw',
            }}
            title="Secure Session"
          />
        </Modal>
      ) : (
        <LoadingSpinnerPanel fullScreen />
      )}
    </YStack>
  );
};
